import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, Leaf, TrendingUp, Shield } from 'lucide-react';
import { heroData, testimonials } from '../mockData';
import { Button } from '../components/ui/button';

const Home = () => {
  const features = [
    {
      id: "feature-zero-impact",
      icon: Leaf,
      title: "Zero Permanent Impact",
      description: "No impact adjustable leg foundations mean your land returns to nature in 30 days"
    },
    {
      id: "feature-roi",
      icon: TrendingUp,
      title: "Infinate ROI",
      description: "Turn unused corners into your most profitable per-square-meter acreage"
    },
    {
      id: "feature-legacy",
      icon: Shield,
      title: "Preserve Your Legacy",
      description: "Fund estate operations while protecting indigenous flora and heritage"
    }
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Legacy & Landscapes | Luxury Glamping Investment South Africa</title>
        <meta name="description" content="Transform your scenic South African property into a luxury glamping destination. Zero-impact structures, high ROI from R650,000 annually per unit." />
        <link rel="canonical" href="https://goglamping.co.za/" />
      </Helmet>
      
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src={heroData.image}
            alt="Luxury glamping in nature"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-stone-900/70 via-stone-900/50 to-stone-900/70" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            {heroData.title}
          </h1>
          <p className="text-2xl md:text-3xl text-sage-300 mb-8 font-light">
            {heroData.subtitle}
          </p>
          <p className="text-lg md:text-xl text-white/90 mb-12 max-w-3xl mx-auto leading-relaxed">
            {heroData.description}
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-sage-700 hover:bg-sage-800 text-white px-8 py-6 text-lg rounded-lg shadow-2xl hover:shadow-sage-700/50 transition-all duration-300 hover:scale-105">
              {heroData.cta}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
          <div className="animate-bounce">
            <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
              <div className="w-1 h-3 bg-white/50 rounded-full mt-2 animate-pulse" />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-stone-900 mb-4">
              The Big Quiet Narrative
            </h2>
            <p className="text-xl text-stone-600 max-w-3xl mx-auto">
              Position your land as an exclusive holliday santuary for those with bone-deep exhaustion, offering guests a biological reset from the High-Beta stress of modern life
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature) => {
              const Icon = feature.icon;
              return (
                <div
                  key={feature.id}
                  className="bg-stone-50 p-8 rounded-2xl hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-stone-200"
                >
                  <div className="bg-sage-700 w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                    <Icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-stone-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-stone-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Market Opportunity Teaser */}
      <section className="py-24 bg-gradient-to-br from-stone-900 to-stone-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                The "Goldilocks" Window
              </h2>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                The South African luxury travel market is projected to reach Billions of Rands by 2030, growing at 10.1% annually. The eco-luxury glamping niche? An explosive 11.5% growth rate.
              </p>
              <p className="text-lg text-gray-400 mb-8">
                While traditional hotels fight over mass tourism recovery, your land offers what high-net-worth travelers crave: off-the-beaten-track authenticity and exclusivity.
              </p>
              <Link to="/opportunity">
                <Button variant="outline" className="border-sage-400 text-sage-400 hover:bg-sage-400 hover:text-white px-6 py-5 text-base transition-all duration-300">
                  Explore The Opportunity
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20">
                <div className="text-3xl font-bold text-sage-400 mb-2">Billions of Rands</div>
                <div className="text-sm text-gray-300">Market Size by 2030</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20">
                <div className="text-4xl font-bold text-sage-400 mb-2">10.1%</div>
                <div className="text-sm text-gray-300">Annual Growth Rate</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20">
                <div className="text-4xl font-bold text-sage-400 mb-2">Infinate</div>
                <div className="text-sm text-gray-300">Projected ROI</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20">
                <div className="text-2xl font-bold text-sage-400 mb-2">Revenue > R600K pa</div>
                <div className="text-sm text-gray-300">Revenue Per Unit</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-stone-900 mb-4">
              Landowners Like You
            </h2>
            <p className="text-xl text-stone-600">
              Already building their legacy
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <svg
                      key={`star-${testimonial.id}-${i}`}
                      className="w-5 h-5 text-sage-600"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-stone-700 mb-6 leading-relaxed italic">
                  "{testimonial.quote}"
                </p>
                <div>
                  {testimonial.name && (
                    <div className="font-semibold text-stone-900">{testimonial.name}</div>
                  )}
                  <div className="text-sm text-stone-600">{testimonial.location}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-sage-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Don't Just Own the View
          </h2>
          <p className="text-xl mb-8 text-sage-100">
            Share it, preserve it, and let it build your family's next chapter
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-sage-700 hover:bg-gray-100 px-8 py-6 text-lg rounded-lg shadow-2xl hover:scale-105 transition-all duration-300">
              Let's Find the Gold in Your Views
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
